import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';

/**
 * Analytics providers — toggle which third-party tools Spotlight
 * fans events out to.
 *
 * Every impression, CTA click, tour-start / step / complete fires
 * both into Spotlight's own backend AND (for each enabled provider
 * whose global is present on the page) into the provider via its
 * normal `capture` / `track` / `event` call.  No extra network
 * hop from the SDK — the host page's analytics bundle does the
 * real I/O.  Canonical event names listed in docs.
 */

const KNOWN_PROVIDERS = [
  {
    id: 'posthog',
    label: 'PostHog',
    detect: 'window.posthog.capture',
    docs: '/docs/playbooks/posthog',
  },
  {
    id: 'segment',
    label: 'Segment',
    detect: 'window.analytics.track',
  },
  {
    id: 'gtag',
    label: 'Google Analytics (gtag)',
    detect: 'window.gtag',
  },
  {
    id: 'mixpanel',
    label: 'Mixpanel',
    detect: 'window.mixpanel.track',
  },
  {
    id: 'amplitude',
    label: 'Amplitude',
    detect: 'window.amplitude.track',
  },
  {
    id: 'plausible',
    label: 'Plausible',
    detect: 'window.plausible',
  },
];

const CANONICAL_EVENTS = [
  ['spotlight.impression', 'Any piece of content rendered on screen.'],
  ['spotlight.click', 'A non-CTA click registered on content.'],
  ['spotlight.cta_clicked', 'Primary CTA on a tooltip / modal / banner / hotspot.'],
  ['spotlight.dismissed', 'User closed content via X, Escape, or backdrop.'],
  ['spotlight.tour_started', 'Tour opened (welcome page or step 1).'],
  ['spotlight.tour_step_viewed', 'Tour advanced to a new step.'],
  ['spotlight.tour_completed', 'User reached the finish page.'],
  ['spotlight.tour_skipped', 'User closed the tour before finishing.'],
  ['spotlight.hotspot_expanded', 'Hotspot card was opened.'],
];

export function Analytics() {
  const { getToken } = useAuth();
  const { project } = useOutletContext() || {};
  const projectId = project?.project_id;
  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );

  const [enabled, setEnabled] = useState([]);
  const [tenantFallback, setTenantFallback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      // Project row carries the override; tenant config provides the
      // fallback list we show as "defaulting to these" when the
      // project hasn't chosen anything itself.
      const [proj, cfg] = await Promise.all([
        api(`/v1/admin/projects/${encodeURIComponent(projectId)}`),
        api('/v1/admin/config').catch(() => null),
      ]);
      setEnabled(proj?.analytics_providers || []);
      setTenantFallback(cfg?.analytics_providers || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function toggle(id, next) {
    const nextList = next
      ? Array.from(new Set([...(enabled.length ? enabled : tenantFallback), id]))
      : (enabled.length ? enabled : tenantFallback).filter((p) => p !== id);
    setSaving(true);
    setNotice(null);
    try {
      // PATCH the project row — empty list clears the override and
      // falls back to the tenant-level default.
      await api(`/v1/admin/projects/${encodeURIComponent(projectId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ analytics_providers: nextList }),
      });
      setEnabled(nextList);
      setNotice('Saved. Clients pick up the change on next SDK config refresh (~30s).');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // What's actually in force for this project — override if set,
  // otherwise the tenant default.
  const effective = enabled.length > 0 ? enabled : tenantFallback;

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Analytics providers</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          Forward every Spotlight event to the analytics tool your
          team already uses — scoped to this project. One project
          can fan out to PostHog; another can stay silent.
          {enabled.length === 0 && tenantFallback.length > 0 && (
            <> Currently defaulting to the tenant's providers:
              <code style={{ marginLeft: 4 }}>{tenantFallback.join(', ')}</code>.
              Toggle something below to override.
            </>
          )}
        </p>
      </div>

      {loading && <div className="state"><div className="state__title">Loading config…</div></div>}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load config</div>
          <div>{error}</div>
        </div>
      )}
      {notice && <div className="state"><div>{notice}</div></div>}

      {!loading && !error && (
        <>
          <ul className="list">
            {KNOWN_PROVIDERS.map((p) => {
              const on = effective.includes(p.id);
              return (
                <li key={p.id} className="list__row">
                  <div className="list__primary">
                    <span className="list__name">{p.label}</span>
                    <span className="list__meta">
                      Detected via <code>{p.detect}</code>
                    </span>
                  </div>
                  <label className="provider-switch">
                    <input
                      type="checkbox"
                      disabled={saving}
                      checked={on}
                      onChange={(e) => toggle(p.id, e.target.checked)}
                    />
                    <span>{on ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </li>
              );
            })}
          </ul>

          <section className="card" style={{ marginTop: 8 }}>
            <h3 className="card__title">What the SDK forwards</h3>
            <p className="card__body">
              Nine canonical event names, named to stay stable so your
              dashboards don't break when we ship SDK updates. Every
              event carries <code>content_id</code> plus
              content-specific props (step index, URL, target selector).
            </p>
            <table className="events-table">
              <thead>
                <tr><th>Event name</th><th>Fires when</th></tr>
              </thead>
              <tbody>
                {CANONICAL_EVENTS.map(([name, desc]) => (
                  <tr key={name}>
                    <td><code>{name}</code></td>
                    <td>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card">
            <h3 className="card__title">PostHog: funnels &amp; dashboards</h3>
            <p className="card__body">
              See the <a href="/docs/playbooks/posthog" target="_blank" rel="noreferrer">PostHog playbook</a>
              {' '}for copy-pasteable Insight definitions (tour completion
              funnel, drop-off-by-step, CTA click-through by page) and
              how to wire identify() so user properties line up with
              Spotlight's <code>user_id</code>.
            </p>
          </section>
        </>
      )}
      <style>{ANALYTICS_CSS}</style>
    </section>
  );
}

const ANALYTICS_CSS = `
  .provider-switch {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    font-size: 13px;
    color: var(--text-2);
  }
  .provider-switch input {
    width: 36px;
    height: 20px;
    appearance: none;
    -webkit-appearance: none;
    background: var(--surface-muted);
    border-radius: var(--r-pill);
    position: relative;
    cursor: pointer;
    transition: background 140ms ease;
  }
  .provider-switch input::before {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    top: 2px;
    left: 2px;
    transition: left 140ms ease;
  }
  .provider-switch input:checked {
    background: var(--brand-600);
  }
  .provider-switch input:checked::before {
    left: 18px;
  }
  .events-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-top: 10px;
  }
  .events-table th,
  .events-table td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid var(--border-1);
  }
  .events-table th {
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-3);
  }
  .events-table tbody tr:last-child td {
    border-bottom: none;
  }
`;
